import { Component, OnInit, inject, signal, computed, DestroyRef, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { takeUntil } from 'rxjs';

// Kendo UI Modules
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { GridModule } from '@progress/kendo-angular-grid';

// Icons
import { arrowLeftIcon, saveIcon, cancelIcon, trashIcon, infoCircleIcon, xIcon } from '@progress/kendo-svg-icons';

// Models
import { Organization } from 'src/app/core/models/entities';

// Services
import { OrganizationService } from 'src/app/features/admin/organizations/services/organization.service';
import { Events, EventService } from 'src/app/core/services/event.service';

// Base Component
import { PopupHostComponent } from 'src/app/core/components/popup-host.component';

// Import the new models
import {
  ServiceType,
  ModificationAction,
  ServiceInfo,
  ServiceModification,
  SERVICE_CONFIGURATIONS,
} from './models/customer-details.model';

// Import the slide-out component
import {
  ServiceModificationSlideoutComponent,
  ServiceModificationSlideoutData,
  ServiceModificationSlideoutResult,
} from './components/service-modification-slideout';

// Import the service widget component
import { ServiceWidgetComponent, ServiceCancellationEvent } from './components/service-widget';

@Component({
  selector: 'app-customer-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonsModule,
    DropDownsModule,
    InputsModule,
    DateInputsModule,
    SwitchModule,
    SVGIconModule,
    IndicatorsModule,
    LayoutModule,
    GridModule,
    ServiceWidgetComponent,
  ],
  templateUrl: './customer-details.component.html',
  styleUrls: ['./customer-details.component.scss'],
  providers: [OrganizationService],
})
export class CustomerDetailsComponent extends PopupHostComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly organizationService = inject(OrganizationService);
  private readonly eventService = inject(EventService);

  // Data signals
  public organization = signal<Organization | null>(null);
  public isLoading = signal(false);
  public error = signal<string | null>(null);
  public isSaving = signal(false);
  public showCloseAccountDialog = signal(false);

  // Form
  public modificationForm: FormGroup;

  // Kendo SVG Icons
  public arrowLeftIcon = arrowLeftIcon;
  public saveIcon = saveIcon;
  public cancelIcon = cancelIcon;
  public trashIcon = trashIcon;
  public warningIcon = infoCircleIcon;
  public closeAccountIcon = xIcon;

  constructor() {
    super();
    this.modificationForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadCustomerData();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      customerId: ['', [Validators.required]],
      customerName: ['', [Validators.required]],
      customerSince: ['', [Validators.required]],
      customerType: ['', [Validators.required]],
      primaryContact: ['', [Validators.required]],
      primaryContactEmail: ['', [Validators.required]],
    });
  }

  private loadCustomerData(): void {
    this.isLoading.set(true);
    this.error.set(null);

    const organizationId = this.route.snapshot.queryParams['organizationId'];

    if (!organizationId) {
      this.error.set('No organization ID provided');
      this.isLoading.set(false);
      return;
    }

    // Load organization data
    this.organizationService
      .getOrganizationById(organizationId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (org: Organization) => {
          this.organization.set(org);
          this.populateFormWithOrganization(org);
          this.isLoading.set(false);
        },
        (err: any) => {
          this.error.set('Failed to load organization data');
          console.error('Error loading organization:', err);
          this.isLoading.set(false);
        }
      );
  }

  private populateFormWithOrganization(org: Organization): void {
    this.modificationForm.patchValue({
      customerId: org.id,
      customerName: org.name,
      customerSince: org.created_at,
      customerType: org.account_type,
      primaryContact: org.owner,
      primaryContactEmail: org.owner_email,
    });
  }

  public onBack(): void {
    this.router.navigate(['/customer/msp-portal/customers']);
  }

  public onSave(): void {
    if (this.modificationForm.valid) {
      this.isSaving.set(true);

      const formData = this.modificationForm.value;

      // TODO: Implement service modification logic
      console.log('Saving service modifications:', formData);

      // Simulate API call
      setTimeout(() => {
        this.isSaving.set(false);
        // Navigate back to customers page with success message
        this.router.navigate(['/customer/msp-portal/customers'], {
          queryParams: { modificationSuccess: 'true' },
        });
      }, 2000);
    }
  }

  public onCancel(): void {
    this.router.navigate(['/customer/msp-portal/customers']);
  }

  public onCloseAccount(): void {
    this.showCloseAccountDialog.set(true);
  }

  public onCloseAccountDialogClose(): void {
    this.showCloseAccountDialog.set(false);
  }

  public onConfirmCloseAccount(): void {
    // TODO: Implement actual account closure logic
    console.log('Closing account for customer:', this.modificationForm.get('customerName')?.value);

    // Simulate API call
    this.isSaving.set(true);

    // TODO: Replace with actual API call
    // For now, we'll simulate a successful API call
    // In a real implementation, you would call the API and handle the response
    // this.organizationService.closeAccount(this.organization()?.id).subscribe({
    //   next: () => {
    //     this.isSaving.set(false);
    //     this.showCloseAccountDialog.set(false);
    //     this.router.navigate(['/customer/msp-portal/customers'], {
    //       queryParams: { accountClosed: 'true' },
    //     });
    //   },
    //   error: (error) => {
    //     console.error('Failed to close account:', error);
    //     this.isSaving.set(false);
    //     // Handle error - show error message to user
    //   }
    // });

    // Simulate successful API call after 2 seconds
    setTimeout(() => {
      this.isSaving.set(false);
      this.showCloseAccountDialog.set(false);

      // Navigate back to customers page with success message
      this.router.navigate(['/customer/msp-portal/customers'], {
        queryParams: { accountClosed: 'true' },
      });
    }, 2000);
  }

  public addServiceModification(serviceType?: ServiceType, existingService?: ServiceInfo): void {
    const existingServices = this.getCurrentCustomerServices();

    const slideoutData: ServiceModificationSlideoutData = {
      serviceType: serviceType,
      action: existingService ? ModificationAction.MODIFY : ModificationAction.ADD,
      existingService,
      existingServices,
    };

    console.log('Opening service modification slide-out with:', slideoutData);

    const options = {
      animate: { type: 'slide', direction: 'left', duration: 200 },
      anchorAlign: { horizontal: 'right', vertical: 'top' },
      popupAlign: { horizontal: 'right', vertical: 'top' },
    };

    this.openPopup(this.bodyElement, ServiceModificationSlideoutComponent, options);

    setTimeout(() => {
      const slideOutComponent = this.popupRef?.content.instance as ServiceModificationSlideoutComponent;
      if (slideOutComponent) {
        // Set the slideout data using a method call
        if (typeof slideOutComponent.setSlideoutData === 'function') {
          slideOutComponent.setSlideoutData(slideoutData);
        }

        // Subscribe to the cancelled output
        slideOutComponent.cancelled.subscribe(() => {
          this.closePopup();
        });

        // Subscribe to the result output
        slideOutComponent.result.subscribe((result) => {
          this.onSlideoutResult(result);
          this.closePopup();
        });
      }
    });
  }

  public onSlideoutResult(result: ServiceModificationSlideoutResult): void {
    if (result) {
      // TODO: Handle the modification result
      console.log('Service modification result:', result);
    }
  }

  public handleServiceCancellation(cancellationEvent: ServiceCancellationEvent): void {
    console.log('Service cancellation event:', cancellationEvent);

    // TODO: Implement API call to cancel the service
    // This would typically involve:
    // 1. Calling the service cancellation API
    // 2. Updating the local service list
    // 3. Refreshing the customer's services

    // For now, we'll just log the cancellation and show a placeholder
    console.log(
      `Service "${cancellationEvent.service.serviceName}" cancelled with reason: ${cancellationEvent.cancellationReason}`
    );

    // TODO: Refetch current services for the customer
    // this.refreshCustomerServices();
  }

  public getServiceTypeDisplayName(serviceType: ServiceType): string {
    const config = SERVICE_CONFIGURATIONS[serviceType];
    return config ? config.title : serviceType;
  }

  public getActionDisplayName(action: ModificationAction): string {
    switch (action) {
      case ModificationAction.ADD:
        return 'Add';
      case ModificationAction.MODIFY:
        return 'Modify';
      case ModificationAction.CANCEL:
        return 'Cancel';
      default:
        return action;
    }
  }

  public getActionBadgeClass(action: ModificationAction): string {
    switch (action) {
      case ModificationAction.ADD:
        return 'bg-success';
      case ModificationAction.MODIFY:
        return 'bg-warning';
      case ModificationAction.CANCEL:
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  public getCurrentCustomerServices(): ServiceType[] {
    // Get the current services from mock data
    const services = this.getMockServices();
    return services.map((service) => service.serviceType);
  }

  public getMockServices(): ServiceInfo[] {
    // TODO: Replace with actual service data from API
    return [
      {
        id: '1',
        serviceName: 'mXDR Complete',
        serviceSku: 'MXDR-COMPLETE',
        status: 'active',
        startDate: new Date('2024-01-01'),
        description: '',
        serviceType: ServiceType.MXDR,
        mxdrType: 'complete' as any,
        extendedLogRetention: true,
        govCloud: false,
        quantityAdUsers: 150,
        quantityServers: 25,
        quantityAppliances: 2,
        virtualAppliance: false,
        applianceModels: ['SC-HW-160'],
        shippingContact: {
          name: 'John Doe',
          phone: '555-123-4567',
          address: '123 Main St',
          city: 'Anytown',
          state: 'CA',
          zipCode: '90210',
        },
        additionalLocations: [],
      },
      //   {
      //     id: '2',
      //     serviceName: 'mEDR Premier',
      //     serviceSku: 'MEDR-PREMIER',
      //     status: 'active',
      //     startDate: new Date('2024-02-01'),
      //     description: 'Managed Endpoint Detection and Response',
      //     serviceType: ServiceType.MEDR,
      //     medrNetworkDiscovery: true,
      //     totalMachines: 200,
      //   },
      //   {
      //     id: '3',
      //     serviceName: 'VSS Enhanced',
      //     serviceSku: 'VSS-ENHANCED',
      //     status: 'active',
      //     startDate: new Date('2024-03-01'),
      //     description: 'Vulnerability Scanning Service',
      //     serviceType: ServiceType.VSS,
      //     totalMachines: 150,
      //   },
    ];
  }
}
